import { rem } from 'polished'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { sum } from 'lib/array/arrayUtils'

const Container = styled(Clickable)`
  position: relative;
  display: flex;
  height: fit-content;
  align-items: center;
  width: 100%;
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

export const Item = styled.div`
  padding: ${rem(8)} ${rem(16)};
  width: 100%;
  justify-self: stretch;

  &:not(:first-child) {
    border-left: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  }
`

interface GuestsProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  title?: string
  occupancies: Array<App.Occupants>
  isFlights?: boolean
}

export function GuestsInputTrigger({
  onClick,
  title,
  occupancies,
  isFlights = false,
}: GuestsProps) {
  const intl = useIntl()

  const resolvedTitle = title ?? intl.formatMessage({
    id: '8iFkLA68hPV6oR-4DwBC',
    defaultMessage: 'Guests',
    description: 'Default label for guests input trigger in trip planner search',
  })
  const guestsInsteadOfAdults = occupancies.some(
    (room) => room.childrenAge?.length,
  )
  const guestCount = useMemo(
    () =>
      sum(
        occupancies,
        (room) =>
          room.adults + (room.childrenAge ? room.childrenAge.length : 0),
      ),
    [occupancies],
  )

  const guestsLabel = intl.formatMessage(
    {
      id: 'RGq43j-JY7X9ME1K8ugk',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Guest count in occupancy trigger for trip planner search',
    },
    { count: guestCount },
  )
  const adultsLabel = intl.formatMessage(
    {
      id: 'pdKVhRa5Fq08zk8drxXq',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Adult count in occupancy trigger for trip planner search',
    },
    { count: guestCount },
  )

  // we shouldn't disable this rule, but its the only thing I can think of to localise this sentence fully
  // eslint-disable-next-line formatjs/no-multiple-plurals
  const guestsAndRoomsLabel = intl.formatMessage(
    {
      id: 'DOZk8X-yNtSRFCWW0NWh',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}} · {roomCount, plural, one {# room} other {# rooms}}',
      description: 'Guest and room count in occupancy trigger for trip planner search',
    },
    { guestCount, roomCount: occupancies.length },
  )

  // eslint-disable-next-line formatjs/no-multiple-plurals
  const adultsAndRoomsLabel = intl.formatMessage(
    {
      id: 'MR6cqMRu8TSHlO4tnbzJ',
      defaultMessage: '{adultCount, plural, one {# adult} other {# adults}} · {roomCount, plural, one {# room} other {# rooms}}',
      description: 'Adult and room count in occupancy trigger for trip planner search',
    },
    { adultCount: guestCount, roomCount: occupancies.length },
  )

  const peopleLabel = guestsInsteadOfAdults ? guestsLabel : adultsLabel
  const peopleAndRoomsLabel = guestsInsteadOfAdults ? guestsAndRoomsLabel : adultsAndRoomsLabel

  return (
    <Container onClick={onClick}>
      <Item>
        <Caption variant="large" weight="bold">
          {resolvedTitle}
        </Caption>
        <BodyText variant="medium">
          {isFlights ? peopleLabel : peopleAndRoomsLabel}
        </BodyText>
      </Item>
    </Container>
  )
}
